import { type FacebookCookieCredentials } from '@unipile/provider-facebook';
import { Alert, AlertDescription, Button, Field, FieldGroup, FieldLabel, Input, LocationConfig, Spinner } from '@unipile/ui';
import { InfoIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type CookiesFormValues = FacebookCookieCredentials;

export function CookiesForm({
  onSubmit,
  isPending,
}: {
  onSubmit: (data: FacebookCookieCredentials) => void;
  isPending?: boolean;
}) {
  const form = useForm<CookiesFormValues>({
    defaultValues: {
      xs: '',
      c_user: '',
      datr: '',
      sb: '',
      fr: '',
    },
  });
  const { t } = useTranslation();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
      <Alert>
        <InfoIcon />
        <AlertDescription>{t('LoginScreen.cookies.warning', { ns: 'facebook' })}</AlertDescription>
      </Alert>

      <FieldGroup>
        <Controller
          control={form.control}
          name="xs"
          rules={{ required: t('LoginScreen.form.xs.required', { ns: 'facebook' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.xs.label', { ns: 'facebook' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">xs</code>
              </FieldLabel>
              <Input placeholder="1234567890abcdef" {...field} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="c_user"
          rules={{ required: t('LoginScreen.form.c_user.required', { ns: 'facebook' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.c_user.label', { ns: 'facebook' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">c_user</code>
              </FieldLabel>
              <Input placeholder="1000..." {...field} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="datr"
          rules={{ required: t('LoginScreen.form.datr.required', { ns: 'facebook' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.datr.label', { ns: 'facebook' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">datr</code>
              </FieldLabel>
              <Input placeholder="abcdef1234567890" {...field} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="sb"
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.sb.label', { ns: 'facebook' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">sb</code>
              </FieldLabel>
              <Input placeholder={t('LoginScreen.form.optional.placeholder', { ns: 'facebook' })} {...field} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="fr"
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('LoginScreen.form.fr.label', { ns: 'facebook' })}
                <code className="text-muted-foreground bg-muted ml-1 rounded-md px-1 text-xs">fr</code>
              </FieldLabel>
              <Input placeholder={t('LoginScreen.form.optional.placeholder', { ns: 'facebook' })} {...field} />
            </Field>
          )}
        />

        <LocationConfig provider="facebook" />

        <Button type="submit" disabled={isPending}>
          {isPending && <Spinner />}
          {t('Common.next')}
        </Button>
      </FieldGroup>
    </form>
  );
}
